import { BrokenImage, Delete, PictureAsPdf } from '@mui/icons-material';
import { Box, Card, CardMedia, CircularProgress, IconButton, Typography } from '@mui/material';

import type { DeliveryNotePhoto } from '../../../../services/goods-receiving/goods-receiving.types';
import { useDeletePhoto } from '../../hooks/use-draft-mutations';
import { usePresignedUrls } from '../../hooks/use-presigned-urls';

interface ExistingPhotosGridProps {
  photos: DeliveryNotePhoto[];
  deliveryNoteId: string;
  onPhotoDeleted?: () => void;
}

function isPdfFilename(filename: string): boolean {
  return filename.toLowerCase().endsWith('.pdf');
}

// A photo is rendered as a PDF icon only when it has no storageKey yet
// (pending preview generation). Once the backend updates storageKey after
// the preview job completes, it should render as an image thumbnail.
function shouldRenderAsPdfIcon(photo: DeliveryNotePhoto): boolean {
  return isPdfFilename(photo.originalFilename) && !photo.storageKey;
}

const previewBoxSx = {
  height: 150,
  bgcolor: 'grey.100',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0.5,
} as const;

function PdfPreview({ filename }: { filename: string }) {
  return (
    <Box sx={previewBoxSx}>
      <PictureAsPdf sx={{ fontSize: 48, color: 'error.main' }} />
      <Typography variant="caption" noWrap sx={{ maxWidth: '90%', textAlign: 'center' }}>
        {filename}
      </Typography>
    </Box>
  );
}

function BrokenPreview({ filename }: { filename: string }) {
  return (
    <Box sx={previewBoxSx}>
      <BrokenImage sx={{ fontSize: 40, color: 'text.disabled' }} />
      <Typography
        variant="caption"
        noWrap
        sx={{
          color: 'text.secondary',
          maxWidth: '90%',
          textAlign: 'center',
        }}
      >
        {filename}
      </Typography>
    </Box>
  );
}

function LoadingPreview() {
  return (
    <Box
      sx={{
        height: 150,
        bgcolor: 'grey.100',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <CircularProgress size={24} />
    </Box>
  );
}

function renderPreview(args: {
  isPdf: boolean;
  failed: boolean;
  url: string | undefined;
  isLoading: boolean;
  filename: string;
}): React.ReactNode {
  if (args.isPdf) {
    return <PdfPreview filename={args.filename} />;
  }
  if (args.failed) {
    return <BrokenPreview filename={args.filename} />;
  }
  if (args.isLoading || !args.url) {
    return <LoadingPreview />;
  }
  return (
    <CardMedia
      component="img"
      image={args.url}
      alt={args.filename}
      sx={{ height: 150, objectFit: 'cover' }}
    />
  );
}

export function ExistingPhotosGrid({
  photos,
  deliveryNoteId,
  onPhotoDeleted,
}: ExistingPhotosGridProps) {
  const deletePhoto = useDeletePhoto();

  // Photos that need a presigned URL: non-PDFs + PDFs whose storageKey was
  // already resolved by the preview job (shouldRenderAsPdfIcon returns false).
  const photosNeedingUrl = photos.filter((p) => !shouldRenderAsPdfIcon(p) && !!p.storageKey);
  const { photoUrls, isLoading } = usePresignedUrls(
    photosNeedingUrl.map((p) => ({ id: p.id, storageKey: p.storageKey })),
  );

  // Photos whose URL failed to load show a broken-image placeholder instead
  // of a stuck spinner. We derive this from the hook result: once loading is
  // complete, any photo-needing-url without a URL in the map must have failed.
  const failedIds = new Set(
    photosNeedingUrl.filter((p) => !isLoading && !photoUrls.has(p.id)).map((p) => p.id),
  );

  const handleDelete = (photoId: string) => {
    deletePhoto.mutate({ deliveryNoteId, photoId }, { onSuccess: () => onPhotoDeleted?.() });
  };

  if (photos.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(3, 1fr)',
          md: 'repeat(4, 1fr)',
        },
        gap: 2,
      }}
    >
      {photos.map((photo) => {
        const preview = renderPreview({
          isPdf: shouldRenderAsPdfIcon(photo),
          failed: failedIds.has(photo.id),
          url: photoUrls.get(photo.id),
          isLoading,
          filename: photo.originalFilename,
        });

        return (
          <Card key={photo.id} sx={{ position: 'relative' }}>
            {preview}

            <IconButton
              size="small"
              onClick={() => handleDelete(photo.id)}
              disabled={deletePhoto.isPending && deletePhoto.variables?.photoId === photo.id}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'error.main',
                color: 'white',
                '&:hover': { bgcolor: 'error.dark' },
                '&.Mui-disabled': {
                  bgcolor: 'action.disabled',
                  color: 'text.disabled',
                },
              }}
            >
              {deletePhoto.isPending && deletePhoto.variables?.photoId === photo.id ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <Delete fontSize="small" />
              )}
            </IconButton>
          </Card>
        );
      })}
    </Box>
  );
}
